import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { IconFileDollar, IconFileInvoice, IconLayoutDashboard, IconReceiptRefund, IconReportAnalytics } from '@tabler/icons-react';
import { defineModule } from '../types';
import { indexRoute, lazyRoute, routeGroup } from '../routing';
import { DASHBOARD, P } from './perms';

const widgets = () => import('./widgets');
const SalesSnapshotWidget = lazy(() => widgets().then((m) => ({ default: m.SalesSnapshotWidget })));

const dashboard = () => import('./pages/DashboardPage');
const invoices = () => import('./pages/InvoicesPages');
const editor = () => import('./pages/InvoiceEditorPage');
const preview = () => import('./pages/InvoicePreviewPage');
const quotations = () => import('./pages/QuotationsPages');
const report = () => import('./pages/SalesReportPage');

/**
 * BILL — الفوترة: quotations, tax invoices, credit / debit notes and the sales dashboard.
 * Issuing goes through tax (ZATCA e-invoice) and fin (journal + customer balance) on the server.
 */
export default defineModule({
  code: 'bill',
  nameAr: 'الفوترة',
  nameEn: 'Billing',
  icon: IconFileInvoice,
  order: 5,
  menu: [
    { key: 'bill.dashboard', labelKey: 'bill:nav.dashboard', path: '/bill/dashboard', icon: IconLayoutDashboard, permission: DASHBOARD },
    { key: 'bill.invoices', labelKey: 'bill:nav.invoices', path: '/bill/invoices', icon: IconFileInvoice, permission: P.invoice.read },
    { key: 'bill.quotations', labelKey: 'bill:nav.quotations', path: '/bill/quotations', icon: IconFileDollar, permission: P.quotation.read },
    { key: 'bill.notes', labelKey: 'bill:nav.notes', path: '/bill/notes', icon: IconReceiptRefund, permission: P.invoice.read },
    { key: 'bill.reports', labelKey: 'bill:nav.reports', path: '/bill/reports', icon: IconReportAnalytics, permission: P.report.read },
  ],
  routes: {
    admin: [
      // `/bill` opens the dashboard; its own path keeps the sidebar from highlighting it on every billing page
      { path: 'bill', element: <Navigate to="/bill/dashboard" replace /> },
      lazyRoute('bill/dashboard', () => dashboard().then((m) => ({ default: m.SalesDashboardPage })), { permission: DASHBOARD, crumb: 'bill:nav.dashboard' }),
      routeGroup('bill/invoices', 'bill:nav.invoices', [
        indexRoute(() => invoices().then((m) => ({ default: m.InvoicesPage })), P.invoice.read),
        lazyRoute('new', () => editor().then((m) => ({ default: m.NewInvoicePage })), { permission: P.invoice.create, crumb: 'bill:crumb.newInvoice' }),
        lazyRoute(':id', () => invoices().then((m) => ({ default: m.InvoicePage })), { permission: P.invoice.read, crumb: 'bill:invoice.detail' }),
        lazyRoute(':id/edit', () => editor().then((m) => ({ default: m.EditInvoicePage })), { permission: P.invoice.create, crumb: 'bill:crumb.editDraft' }),
        lazyRoute(':id/preview', () => preview().then((m) => ({ default: m.InvoicePreviewPage })), { permission: P.invoice.read, crumb: 'bill:crumb.preview' }),
        lazyRoute(':id/revise', () => editor().then((m) => ({ default: m.ReviseInvoicePage })), { permission: P.invoice.revise, crumb: 'bill:crumb.revise' }),
        lazyRoute(':id/credit-note', () => editor().then((m) => ({ default: m.CreditNotePage })), { permission: P.invoice.create, crumb: 'bill:crumb.creditNote' }),
        lazyRoute(':id/debit-note', () => editor().then((m) => ({ default: m.DebitNotePage })), { permission: P.invoice.create, crumb: 'bill:crumb.debitNote' }),
      ]),
      routeGroup('bill/notes', 'bill:nav.notes', [indexRoute(() => invoices().then((m) => ({ default: m.NotesPage })), P.invoice.read)]),
      routeGroup('bill/quotations', 'bill:nav.quotations', [
        indexRoute(() => quotations().then((m) => ({ default: m.QuotationsPage })), P.quotation.read),
        lazyRoute('new', () => quotations().then((m) => ({ default: m.NewQuotationPage })), { permission: P.quotation.manage, crumb: 'bill:crumb.newQuotation' }),
        lazyRoute(':id', () => quotations().then((m) => ({ default: m.QuotationPage })), { permission: P.quotation.read, crumb: 'bill:quote.detail' }),
        lazyRoute(':id/edit', () => quotations().then((m) => ({ default: m.EditQuotationPage })), { permission: P.quotation.manage, crumb: 'bill:crumb.editQuotation' }),
      ]),
      lazyRoute('bill/reports', () => report().then((m) => ({ default: m.SalesReportPage })), { permission: P.report.read, crumb: 'bill:nav.reports' }),
    ],
  },
  dashboards: [
    { key: 'bill.sales', titleKey: 'bill:widgets.sales', permission: DASHBOARD, component: SalesSnapshotWidget, span: { base: 12, md: 6, lg: 4 }, order: 0 },
  ],
});
